/**
 * HTML sanitization for server-supplied markup.
 *
 * Anything rendered through `dangerouslySetInnerHTML` MUST pass through here
 * first. The terms/conditions content arrives as raw HTML from the API, so a
 * compromised or mis-edited terms record would otherwise execute script in
 * every user's browser (registry S03/S04).
 */

import DOMPurify from 'dompurify';

/**
 * Forces safe link behaviour on anything that opens a new tab.
 *
 * `target="_blank"` without `rel="noopener"` hands the opened page a reference
 * back to ours via `window.opener` (reverse tabnabbing). The terms content is
 * link-heavy, so we normalise rather than strip the attribute.
 */
let hookInstalled = false;

function installLinkHook(): void {
  if (hookInstalled) return;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof Element && node.hasAttribute('target')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  hookInstalled = true;
}

/**
 * Sanitizes an HTML string for use with `dangerouslySetInnerHTML`.
 *
 * Keeps the formatting the terms documents rely on (headings, lists, tables,
 * links, emphasis) and drops anything executable: `<script>`, `<iframe>`,
 * `on*` handlers, `javascript:` URLs, and form controls.
 *
 * @param dirty Untrusted HTML, typically straight off the API.
 * @returns Sanitized HTML, or `''` for null/undefined/empty input.
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';

  installLinkHook();

  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
    // `target` is not allowed by the html profile on its own; we permit it and
    // then harden it in the hook above.
    ADD_ATTR: ['target'],
    // Interactive/embedded content has no place in a terms document.
    FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea', 'iframe', 'object', 'embed'],
  });
}
